import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { Award, Check, Gamepad2, ListChecks, Play } from 'lucide-react';
import { motion } from 'motion/react';
import { Link } from 'react-router';
import { api } from '../../lib/api';
import { useMe } from '../../lib/session';
import type { StudentHome } from '../../lib/types';
import { Button, ButtonLink, Notice, Spinner, Stars } from '../../ui';
import { StudentShell } from './StudentShell';

const STAGE_COPY = {
  not_started: { label: 'Start', icon: Play },
  video: { label: 'Keep watching', icon: Play },
  lab: { label: 'Play the game', icon: Gamepad2 },
  assessment: { label: 'Finish the quiz', icon: ListChecks },
  complete: { label: 'Play again', icon: Check },
} as const;

export function StudentHomePage() {
  const me = useMe();
  const home = useQuery({ queryKey: ['student-home'], queryFn: () => api.get<StudentHome>('/student/home') });
  const firstName = me.data?.kind === 'student' ? me.data.student.displayName : '';

  if (home.isLoading) return <StudentShell><Spinner label="Getting your lessons" /></StudentShell>;
  if (home.isError || !home.data)
    return (
      <StudentShell>
        <div className="mx-auto max-w-5xl px-4 py-10">
          <Notice tone="error">We could not load your lessons. Please try again.</Notice>
          <Button className="mt-4" onClick={() => void home.refetch()}>Try again</Button>
        </div>
      </StudentShell>
    );

  const { lessons, totals } = home.data;
  const next = lessons.find((l) => l.stage !== 'complete') ?? lessons[0];
  const doneCount = lessons.filter((l) => l.stage === 'complete').length;
  const unitTitle = lessons[0]?.unit.title;

  return (
    <StudentShell stars={totals.stars}>
      <main className="mx-auto max-w-5xl px-4 pb-16 pt-8">
        <section className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="font-semibold text-brand">Hello {firstName}!</p>
            <h1 className="mt-1 text-3xl font-extrabold sm:text-4xl">{unitTitle ?? 'Your lessons'}</h1>
            <p className="mt-2 text-muted">
              {doneCount} of {lessons.length} lessons done
              {totals.badges > 0 && ` · ${totals.badges} badge${totals.badges > 1 ? 's' : ''}`}
            </p>
          </div>
          {next && (
            <ButtonLink to={`/learn/lesson/${next.lessonVersionId}`} size="xl" variant="gold" icon={<Play className="size-6 fill-ink" aria-hidden />}>
              {next.stage === 'not_started' ? 'Start next lesson' : 'Continue'}
            </ButtonLink>
          )}
        </section>

        <div className="mt-6 h-3 overflow-hidden rounded-full bg-line" role="progressbar" aria-valuemin={0} aria-valuemax={lessons.length} aria-valuenow={doneCount}>
          <motion.div
            className="h-full rounded-full bg-mint"
            initial={{ width: 0 }}
            animate={{ width: `${lessons.length ? (doneCount / lessons.length) * 100 : 0}%` }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
          />
        </div>

        {lessons.length === 0 ? (
          <div className="mt-10 rounded-[var(--radius-card)] border-2 border-dashed border-line-strong p-10 text-center">
            <p className="font-display text-xl font-bold">No lessons yet</p>
            <p className="mt-1 text-muted">Your teacher will add lessons soon.</p>
          </div>
        ) : (
          <ol className="mt-10 flex flex-col gap-4">
            {lessons.map((l, i) => {
              const copy = STAGE_COPY[l.stage];
              const isNext = l.lessonVersionId === next?.lessonVersionId;
              const done = l.stage === 'complete';
              return (
                <motion.li
                  key={l.lessonVersionId}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.06 }}
                >
                  <Link
                    to={`/learn/lesson/${l.lessonVersionId}`}
                    className={clsx(
                      'group flex items-center gap-4 rounded-[var(--radius-card)] bg-surface p-4 shadow-[var(--shadow-card)] transition-shadow hover:shadow-[var(--shadow-lift)] sm:gap-6 sm:p-5',
                      isNext && 'ring-2 ring-brand',
                    )}
                  >
                    <span
                      className={clsx(
                        'flex size-14 shrink-0 items-center justify-center rounded-2xl font-display text-xl font-extrabold',
                        done ? 'bg-mint text-white' : isNext ? 'bg-brand text-white' : 'bg-brand-soft text-brand',
                      )}
                    >
                      {done ? <Check className="size-7" strokeWidth={3} aria-hidden /> : i + 1}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-semibold text-muted">Lesson {l.code.replace(/^C\d+-S/, '')}</span>
                      <span className="block font-display text-lg font-bold leading-snug sm:text-xl">{l.title}</span>
                      {l.objective && <span className="mt-0.5 hidden text-sm text-muted sm:block">{l.objective}</span>}
                    </span>
                    {done ? (
                      <span className="flex flex-col items-end gap-1">
                        <Stars earned={Math.round((l.stars / Math.max(l.maxStars, 1)) * 3)} />
                        <span className="text-xs font-semibold text-muted">{l.stars} stars</span>
                      </span>
                    ) : (
                      <span className={clsx('inline-flex h-11 items-center gap-2 rounded-xl px-4 font-display font-bold', isNext ? 'bg-brand text-white' : 'bg-brand-soft text-brand')}>
                        <copy.icon className="size-4" aria-hidden />
                        <span className="hidden sm:inline">{copy.label}</span>
                      </span>
                    )}
                  </Link>
                </motion.li>
              );
            })}
          </ol>
        )}

        {totals.badges > 0 && (
          <p className="mt-10 flex items-center gap-2 text-sm text-muted">
            <Award className="size-4 text-gold" aria-hidden /> Badges you earn show up at the end of each lesson.
          </p>
        )}
      </main>
    </StudentShell>
  );
}
